import { useCallback, useEffect, useRef } from 'react';
import { Layers, Object3D } from 'three';

import { useDollhouse, useInteractiveObjects, useSelection } from '@spazious/dollhouse-engine';
import { FNodeTypesEnum, getNodeFromObject } from '@spazious/dollhouse-fsetup';
import { traverseUntil } from '@spazious/dollhouse-utils';
import { useEventCallback } from '@spazious/react-hooks';

const getTargetNodeFromObject = (obj: Object3D) => {
  const node = getNodeFromObject(obj);
  if (!node) return null;

  const target = node.type === FNodeTypesEnum.Furniture && node.originator ? node.originator : node;

  return target.type !== FNodeTypesEnum.FGroup ? target : null;
};

const getObjectWithSelector = (scene: Object3D, selector: string): Object3D | null => {
  let found: null | Object3D = null;
  traverseUntil<Object3D>(scene, (child) => {
    if (getNodeFromObject(child)?.selector === selector) {
      found = child;
      return false;
    }
  });
  return found;
};

type SelectionCallback = (pendingSelectors: string[]) => void;

export const useDelayedSelection = (
  onSelect: SelectionCallback | null = null,
  layer?: number | Layers
) => {
  const onSelectCallback = useRef(onSelect);
  const selectors = useRef<string[]>([]);
  const [interactiveObjects] = useInteractiveObjects(layer);
  const { selected, select } = useSelection();
  const scene = useDollhouse(({ scene }) => scene);

  const updateSelections = useCallback(() => {
    if (!selectors.current.length) {
      return;
    }

    const objectsToSelect: Object3D[] = [];
    const foundSelectors: string[] = [];
    interactiveObjects.forEach((obj) => {
      if (!selectors.current.length) {
        return;
      }

      const node = getTargetNodeFromObject(obj);
      const selector = node?.selector;

      if (!selector || !selectors.current.includes(selector)) {
        return;
      }

      const targetObj = getObjectWithSelector(scene, selector);

      if (targetObj) {
        objectsToSelect.push(targetObj);
        foundSelectors.push(selector);
      }
    });

    if (objectsToSelect.length) {
      select(Array.from(new Set([...selected, ...objectsToSelect])));
      selectors.current = selectors.current.filter((s) => !foundSelectors.includes(s));
      onSelectCallback.current?.([...selectors.current]);
    }
  }, [select, selected, interactiveObjects, scene, selectors]);

  const setSelectors = useEventCallback((selectorsToSelect: string[]) => {
    selectors.current = [...selectorsToSelect];
    updateSelections();
  });

  const resetSelection = useEventCallback(() => {
    select([]);
    setSelectors([]);
  });

  useEffect(() => {
    updateSelections();
  }, [updateSelections]);

  return { resetSelection, setSelectors };
};
